import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import KineticTitle from "@/components/KineticTitle";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { BookCard, BookModal } from "@/components/BookCard";
import { site, hero, marquee, home, quote, books } from "@/data/content";

const ease = [0.22, 1, 0.36, 1];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 64]);
  const [selected, setSelected] = useState(null);

  return (
    <>
      <Seo title={site.name + " — Filosofa, scrittrice e poetessa"} description={site.description} />

      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
        data-testid="home-hero"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bordeaux"
              data-testid="hero-eyebrow"
            >
              {hero.eyebrow}
            </motion.p>
            <KineticTitle
              lines={hero.lines}
              delay={0.35}
              className="mt-6 font-medium text-[clamp(2.6rem,6.5vw,5.25rem)] leading-[1.05] tracking-tight"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8, ease }}
              className="mt-8 max-w-xl text-base sm:text-lg font-light leading-relaxed text-ink2"
              data-testid="hero-subtitle"
            >
              {hero.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.8, ease }}
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4"
            >
              <Link
                to={hero.ctas[0].to}
                className="bg-ink text-paper px-8 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors hover:bg-bordeaux"
                data-testid="hero-cta-about"
              >
                {hero.ctas[0].label}
              </Link>
              <Link
                to={hero.ctas[1].to}
                className="link-underline inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em]"
                data-testid="hero-cta-books"
              >
                {hero.ctas[1].label}
                <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div style={{ y }} className="frame-photo max-w-md mx-auto lg:ml-auto">
              <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                animate={{ clipPath: "inset(0% 0 0 0)" }}
                transition={{ delay: 0.5, duration: 1.1, ease }}
              >
                <img
                  src={hero.portrait}
                  alt={hero.portraitAlt}
                  className="w-full aspect-[3/4] object-cover"
                  data-testid="hero-portrait-image"
                />
              </motion.div>
              <p className="mt-6 font-mono text-[9px] uppercase tracking-[0.3em] text-muted3">
                Luisa Miceli — ritratto
              </p>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-3 text-muted3"
          aria-hidden="true"
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.35em]">Scorri</span>
          <ArrowDown size={14} strokeWidth={1.5} className="animate-bounce" />
        </motion.div>
      </section>

      <Marquee items={marquee} />

      <section className="py-24 lg:py-32" data-testid="home-section-pensiero">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <Reveal className="lg:col-span-3">
              <p className="font-mono text-xs tracking-[0.3em] text-bordeaux">{home.pensiero.index}.</p>
            </Reveal>
            <div className="lg:col-span-9 max-w-3xl">
              <Reveal>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  {home.pensiero.title}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-ink2">
                  {home.pensiero.text}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <Link
                  to={home.pensiero.link.to}
                  className="link-underline inline-flex items-center gap-2 mt-8 text-[11px] uppercase tracking-[0.25em]"
                  data-testid="home-pensiero-link"
                >
                  {home.pensiero.link.label}
                  <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="home-section-scrittura">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <Reveal className="lg:col-span-5 order-2 lg:order-1">
              <div className="frame-photo max-w-md">
                <img
                  src={home.scrittura.image}
                  alt={home.scrittura.imageAlt}
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
            </Reveal>
            <div className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
              <Reveal>
                <p className="font-mono text-xs tracking-[0.3em] text-bordeaux">
                  {home.scrittura.index}.
                </p>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  {home.scrittura.title}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 font-serif italic text-xl sm:text-2xl leading-relaxed text-ink max-w-xl">
                  {home.scrittura.text}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <Link
                  to={home.scrittura.link.to}
                  className="link-underline inline-flex items-center gap-2 mt-8 text-[11px] uppercase tracking-[0.25em]"
                  data-testid="home-scrittura-link"
                >
                  {home.scrittura.link.label}
                  <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="home-section-aspace">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <p className="font-mono text-xs tracking-[0.3em] text-bordeaux">{home.aspace.index}.</p>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  {home.aspace.title}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-ink2 max-w-xl">
                  {home.aspace.text}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <Link
                  to={home.aspace.link.to}
                  className="inline-flex items-center gap-2 mt-8 border border-ink px-8 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors hover:bg-ink hover:text-paper"
                  data-testid="home-aspace-link"
                >
                  {home.aspace.link.label}
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              </Reveal>
            </div>
            <Reveal className="lg:col-span-6">
              <img
                src={home.aspace.image}
                alt={home.aspace.imageAlt}
                className="w-full aspect-[16/11] object-cover"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="home-section-libri">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.3em] text-bordeaux">{home.libri.index}.</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl">{home.libri.title}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to={home.libri.link.to}
                className="link-underline inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em]"
                data-testid="home-libri-link"
              >
                {home.libri.link.label}
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-16 lg:max-w-4xl">
            {books.map((book, i) => (
              <Reveal key={book.slug} delay={(i % 2) * 0.08}>
                <BookCard book={book} onOpen={setSelected} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-dark text-paper py-24 lg:py-32" data-testid="home-section-quote">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terracotta">
              Una frase originale
            </p>
            <blockquote className="mt-8 font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-snug">
              «{quote.text}»
            </blockquote>
            <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.3em] text-paper/60">
              {quote.author}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="home-section-contatti">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-tight max-w-3xl">
                {home.contatti.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-ink2 max-w-xl">
                {home.contatti.text}
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-4 lg:text-right">
            <Link
              to="/contatti"
              className="inline-flex items-center gap-3 bg-bordeaux text-paper px-9 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors hover:bg-ink"
              data-testid="home-contatti-cta"
            >
              {home.contatti.cta}
              <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>

      <BookModal book={selected} onClose={() => setSelected(null)} />
    </>
  );
}
